// Affiliate helpers — Phase 3
import { supabase } from "@/integrations/supabase/client";

const AFF_KEY = "wekit_affiliate_code";

export function captureAffiliateFromUrl(code: string) {
  if (!code) return;
  const clean = code.replace(/[^a-zA-Z0-9-]/g, "").toUpperCase();
  if (!clean) return;
  try {
    localStorage.setItem(AFF_KEY, clean);
  } catch { /* ignore */ }
}

export function getStoredAffiliateCode(): string | null {
  try { return localStorage.getItem(AFF_KEY); } catch { return null; }
}

export function clearStoredAffiliate() {
  try { localStorage.removeItem(AFF_KEY); } catch { /* ignore */ }
}

export function buildAffiliateUrl(code: string) {
  return `https://wekit.ai/a/${code}`;
}

export type Affiliate = {
  id: string;
  user_id: string;
  code: string;
  commission_pct: number;
  status: "pending" | "approved" | "suspended";
  payout_email: string | null;
  payout_method: string | null;
  total_earned_inr: number;
  total_paid_inr: number;
};

export async function fetchMyAffiliate(userId: string): Promise<Affiliate | null> {
  const { data } = await supabase
    .from("affiliates")
    .select("id, user_id, code, commission_pct, status, payout_email, payout_method, total_earned_inr, total_paid_inr")
    .eq("user_id", userId)
    .maybeSingle();
  return (data as Affiliate) ?? null;
}

export async function enrollAffiliate(payoutEmail: string, payoutMethod = "upi") {
  const { data, error } = await supabase.rpc("request_affiliate_enrollment", {
    _payout_email: payoutEmail || null,
    _payout_method: payoutMethod,
  });
  if (error) throw error;
  return data as unknown as Affiliate;
}

export type AffiliateConversion = {
  id: string;
  gross_amount_inr: number;
  commission_pct: number;
  commission_amount_inr: number;
  status: "pending" | "approved" | "paid" | "reversed";
  created_at: string;
};

export async function fetchMyConversions(affiliateId: string): Promise<AffiliateConversion[]> {
  const { data } = await supabase
    .from("affiliate_conversions")
    .select("id, gross_amount_inr, commission_pct, commission_amount_inr, status, created_at")
    .eq("affiliate_id", affiliateId)
    .order("created_at", { ascending: false });
  return (data as AffiliateConversion[]) ?? [];
}
